import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { FrozenPane } from 'src/design/atoms/FrozenPane';
import { Center } from 'src/design/atoms/Center';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Tabs } from 'src/design/molecules/Tabs';
import { IconUserCancel } from '@tabler/icons-react';
import { countBy } from 'lodash';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Header } from 'src/design/organisms/Header';
import { TabPanel } from 'src/design/molecules/TabPanel';
import { useFrozenPane } from 'src/design/molecules/useFrozenPane';
import { useAuth } from 'src/hooks/useAuth';
import { useDialogs } from 'src/hooks/useDialogs';
import { useJobs } from 'src/hooks/useJobs';
import { useMenu } from 'src/hooks/useMenu';
import { ActivityFeed } from 'src/routes/CTC/components/ActivityFeed';
import { getErrorMessage } from 'src/utils';

import { BreakdownDialog } from './components/BreakdownDialog';
import { CostCodeTable } from './components/CostCodeTable';
import { Estimator2 } from './components/Estimator2';
import { JobDetails } from './components/JobDetails';
import { JobInfoCard } from './components/JobInfoCard';
import { MaterialsDialog } from './components/MaterialsDialog';
import { ProjectTeamModal } from './components/ProjectTeamModal';
import { useCTCJobView } from './useCTCJobView';

const SUMMARY_GAP = 'sm';

export const ViewCTCJob: React.FC = () => {
  const params: any = useParams();
  const paramsJobId = React.useMemo(
    () => params.id.replaceAll('%20', ' '),
    [params.id],
  );

  const { checkFeatureAllowed } = useAuth();
  const { setSelectedJob } = useJobs();
  const navigate = useNavigate();
  const [selectedLine, setSelectedLine] = React.useState<{
    costCode: string;
    description: string;
    isLabor: boolean;
    isMtd?: boolean;
  } | null>(null);
  const [materialOpen, setMaterialOpen] = React.useState<{
    costCode: string;
    description: string;
    poNumber: string;
    isMtd?: boolean;
  } | null>(null);
  const [tab, setTab] = React.useState<string>('summary');
  const [estimatorCostAtCompletion, setEstimatorCostAtCompletion] =
    React.useState(0);
  const [teamOpen, setTeamOpen] = React.useState(false);
  const [adjustedRev, setAdjustedRev] = React.useState(0);

  const { setCreateJobDialogOpen } = useDialogs();
  const { setDrawerOpen } = useMenu();

  const { data, isLoading, error } = useCTCJobView(paramsJobId);
  const summaryPane = useFrozenPane();

  const hasAdjustmentAccess = checkFeatureAllowed([
    'sys_admin',
    'ctc_adjustments',
  ]);
  const canLinkJob = checkFeatureAllowed(['sys_admin', 'ctc_create_job']);

  React.useEffect(() => {
    setDrawerOpen(false);
  }, [paramsJobId]);

  // Seed adjustedRev synchronously when data first arrives for a job, before
  // Estimator2 mounts — it latches its initial prop once and never re-seeds, so a
  // post-commit effect would leave it stuck at 0. Re-seeds only on job change.
  const seededJobRef = React.useRef<string | null>(null);
  if (data && seededJobRef.current !== paramsJobId) {
    seededJobRef.current = paramsJobId;
    setAdjustedRev(data.adjustedRev);
  }

  // Only sync the global selection once a load succeeds; an errored/loading query
  // must leave the prior selection intact (the original cleared it only after a
  // successful fetch, never on the error path).
  React.useEffect(() => {
    if (data) setSelectedJob(data.linkedJob ?? null);
  }, [data?.linkedJob, data]);

  const getPostCount = React.useMemo(() => {
    if (!data?.linkedJob) {
      return 0;
    }
    const unresolved = countBy(
      data.linkedJob.posts,
      (p) => p.resolved === false && p.ctc === true,
    );
    if (unresolved.true) {
      return unresolved.true;
    }
    return 0;
  }, [data?.linkedJob]);

  const errorMessage = error ? getErrorMessage(error) : null;
  const isAccessDenied =
    !!errorMessage &&
    errorMessage.includes('Request failed with status code 401');
  const showLink = data?.linkedJob && checkFeatureAllowed(['jobs', 'jobs_all']);
  const isActivityFeedAllowed = checkFeatureAllowed(
    ['ctc_activity_feed'],
    'ctc_activity_feed',
  );

  if (isLoading) {
    return (
      <Box style={{ width: '100%', textAlign: 'center' }}>
        <Loader style={{ margin: 'auto', marginTop: '20vh' }} />
        <h3>Loading CTC Insites...</h3>
      </Box>
    );
  }
  return (
    <>
      <Header
        title="CTC"
        breadcrumbProps={{
          items: [
            { text: 'CTC', href: '/ctc' },
            {
              text: data?.job
                ? `${data.job.wsJobNumber} - ${data.job.wsJobName}`
                : '',
            },
          ],
        }}
        helpText="View cost to complete details for this job, including estimator adjustments and cost code breakdowns."
        actions={
          <BasicButton
            onClick={() => {
              navigate('/ctc');
            }}
          >
            Back
          </BasicButton>
        }
      />

      <ProjectTeamModal
        opened={teamOpen}
        linkedJob={data?.linkedJob ?? null}
        onClose={() => setTeamOpen(false)}
      />
      <Tabs value={tab} onChange={(value) => setTab(value ?? 'summary')}>
        <Tabs.List>
          <Tabs.Tab value="summary">CTC Summary</Tabs.Tab>
          {data?.linkedJob && isActivityFeedAllowed && (
            <Tabs.Tab value="activity" disabled={!data?.linkedJob}>
              <Group gap="xs">
                <span>Activity Feed</span>
                {getPostCount > 0 && (
                  <Badge size="sm" circle>
                    {getPostCount}
                  </Badge>
                )}
              </Group>
            </Tabs.Tab>
          )}
        </Tabs.List>
      </Tabs>

      {errorMessage && !isAccessDenied && <p>{errorMessage}</p>}
      {isAccessDenied && (
        <Grid mt="md">
          <Grid.Col span={12}>
            <Center p="xl">
              <Stack align="center" gap="md">
                <IconUserCancel size={54} color="var(--mantine-color-gray-6)" />
                <Title order={3}>Access Denied</Title>
                <Text ta="center">
                  You do not have access to view this job. Please request access
                  from your administrator
                </Text>
                <BasicButton
                  onClick={() => {
                    navigate('/ctc');
                  }}
                >
                  Go Back
                </BasicButton>
              </Stack>
            </Center>
          </Grid.Col>
        </Grid>
      )}
      <TabPanel value="summary" tab={tab}>
        <Stack gap={SUMMARY_GAP}>
          {data?.job && (
            <>
              <BreakdownDialog
                status={data.status}
                costCode={selectedLine?.costCode || null}
                description={selectedLine?.description || null}
                isLabor={selectedLine?.isLabor || false}
                handleClose={() => setSelectedLine(null)}
                jobNumber={data.job.wsJobNumber}
                isMtd={selectedLine?.isMtd}
              />
              <MaterialsDialog
                open={materialOpen !== null}
                costCode={materialOpen?.costCode || null}
                poNumber={materialOpen?.poNumber || null}
                description={materialOpen?.description || null}
                handleClose={() => setMaterialOpen(null)}
                jobNumber={data.job.wsJobNumber}
                totalValue={data.materialsTotal.value}
                totalRemaining={data.materialsTotal.remaining}
              />
              <Group>
                {!data?.linkedJob && canLinkJob && (
                  <BasicButton
                    color="error"
                    mr="xs"
                    variant="outlined"
                    onClick={() => setCreateJobDialogOpen(data.job)}
                  >
                    Sync Job
                  </BasicButton>
                )}
                <BasicButton
                  mr="xs"
                  onClick={() => {
                    navigate(`/ctc/view/${data.nextDetails.previous}`);
                  }}
                  disabled={data.nextDetails.previous.length === 0}
                  variant="outlined"
                >
                  Previous
                </BasicButton>
                <BasicButton
                  onClick={() => {
                    navigate(`/ctc/view/${data.nextDetails.next}`);
                  }}
                  disabled={data.nextDetails.next.length === 0}
                  variant="outlined"
                >
                  Next
                </BasicButton>
              </Group>

              <FrozenPane
                ref={summaryPane.ref}
                frozen={summaryPane.frozen}
                data-testid="ctc-job-summary-frozen"
              >
                <JobInfoCard
                  job={data.job}
                  linkedJob={data.linkedJob}
                  customerName={data.customerName}
                  bonded={data.bonded}
                  showJobLink={Boolean(showLink)}
                  onTeamClick={() => setTeamOpen(true)}
                />
              </FrozenPane>
              <CostCodeTable
                pinnedAbove={
                  summaryPane.frozen
                    ? `calc(${summaryPane.height}px + var(--mantine-spacing-${SUMMARY_GAP}))`
                    : undefined
                }
                summaryList={data.summaryList}
                details={data.details}
                materials={data.materials}
                status={data.status}
                onSelectLine={setSelectedLine}
                onSelectMaterials={setMaterialOpen}
              />

              <Grid>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <JobDetails
                    title="Job To Date"
                    jobData={data.jobData}
                    invoiceData={data.invoices}
                    changeOrderData={data.changeOrders}
                    costAtCompletion={data.jobData.costAtCompletion}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                  <Estimator2
                    jobId={data.job.wsJobNumber}
                    allowed={hasAdjustmentAccess}
                    summary={data.summaryList}
                    adjustments={data.adjustments}
                    initialAdjustedRev={adjustedRev}
                    onAdjustedRevChange={setAdjustedRev}
                    onCostAtCompletionTotalChange={(v) =>
                      setEstimatorCostAtCompletion(v)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <JobDetails
                    title="Operations to Date"
                    jobData={data.jobData}
                    adjusted={adjustedRev}
                    costAtCompletion={estimatorCostAtCompletion}
                    ctc={0}
                    isCalc={true}
                  />
                </Grid.Col>
              </Grid>
            </>
          )}
        </Stack>
      </TabPanel>

      <TabPanel value="activity" tab={tab}>
        {data?.linkedJob && (
          <ActivityFeed jobId={data.linkedJob.id} defaultCTC />
        )}
      </TabPanel>
    </>
  );
};
